var React = require('react');
var { createElement: h, useState } = React;
var SetToVariableForm = require('./SetToVariableForm');

// The small in-row trigger that toggles the inline "set to variable" chooser.
function setVarTriggerButton(open, onToggle) {
  return h('button', {
    type: 'button',
    className: 'jt-setvar-btn' + (open ? ' jt-setvar-btn-active' : ''),
    title: 'Set to variable',
    'aria-label': 'Set to variable',
    onMouseDown: function(e) { e.stopPropagation(); },
    onClick: function(e) { e.preventDefault(); e.stopPropagation(); onToggle(); }
  },
    h('svg', { width: '12', height: '12', viewBox: '0 0 16 16', fill: 'none', stroke: 'currentColor', strokeWidth: '1.6', strokeLinecap: 'round', strokeLinejoin: 'round' },
      h('path', { d: 'M1.5 8h6' }),
      h('path', { d: 'M5 5.5 7.5 8 5 10.5' }),
      h('rect', { x: '10', y: '3', width: '4.5', height: '10', rx: '1.4' })
    )
  );
}

// A parent with more children than this keeps its children collapsed by
// default, so viewing a big list/object doesn't explode into thousands of rows.
var AUTO_EXPAND_SIBLING_LIMIT = 15;

var ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;
var MONTH_NAMES_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function formatDateTimeShort(iso) {
  var d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  var m = MONTH_NAMES_SHORT[d.getMonth()];
  var day = d.getDate();
  var y = d.getFullYear();
  var hr = d.getHours();
  var ampm = hr >= 12 ? 'PM' : 'AM';
  hr = hr % 12 || 12;
  var min = String(d.getMinutes()).padStart(2, '0');
  return m + ' ' + day + ', ' + y + ' ' + hr + ':' + min + ' ' + ampm;
}

function JsonLeaf(props) {
  var val = props.value;
  if (val === null) return h('span', { className: 'jt-null' }, 'null');
  var t = typeof val;
  if (t === 'boolean') return h('span', { className: 'jt-bool' }, String(val));
  if (t === 'number') return h('span', { className: 'jt-num' }, String(val));
  if (t === 'string') {
    if (ISO_DATE_RE.test(val)) {
      var formatted = formatDateTimeShort(val);
      if (formatted) {
        return h('span', { className: 'jt-str jt-datetime', title: val }, formatted);
      }
    }
    if (val === 'true' || val === 'false') {
      return h('span', { className: 'jt-bool' }, val);
    }
    if (val.length > 0 && val.length <= 16 && /^-?\d+(\.\d+)?$/.test(val)) {
      return h('span', { className: 'jt-num' }, val);
    }
    return h('span', { className: 'jt-str' }, val);
  }
  return h('span', { className: 'jt-str' }, String(val));
}

function getJsonNodeLabel(label, depth, isArr) {
  if (label == null) return isArr ? 'List' : 'Details';
  if (isArr && /^\d+$/.test(String(label))) return 'Item ' + (Number(label) + 1);
  return label;
}

function getJsonNodeSummary(isArr, count) {
  return isArr
    ? count + ' ' + (count === 1 ? 'item' : 'items')
    : count + ' ' + (count === 1 ? 'field' : 'fields');
}

function getAutoExpandedPickerRoot(value) {
  var current = value;
  var pathPrefix = '';
  var safety = 0;

  while (current && typeof current === 'object' && !Array.isArray(current) && safety < 5) {
    var keys = Object.keys(current);
    if (keys.length !== 1) break;
    var onlyKey = keys[0];
    var nextValue = current[onlyKey];
    if (!nextValue || typeof nextValue !== 'object') break;
    pathPrefix = pathPrefix ? pathPrefix + '.' + onlyKey : onlyKey;
    current = nextValue;
    safety += 1;
  }

  return { value: current, pathPrefix: pathPrefix };
}

function JsonTreeNode(props) {
  var label = props.label;
  var value = props.value;
  var depth = props.depth || 0;
  // Dot-path from the tree root to this node ('' = the whole output). Threaded
  // down so any row can capture its value into a variable at the right path.
  var path = props.path || '';
  var variableNames = props.variableNames;
  var onSetToVariable = props.onSetToVariable;
  // How many siblings this node has (i.e. the parent's child count). Used to
  // avoid auto-expanding every item of a large collection, which would render
  // thousands of rows at once and lag the canvas. Small groups still auto-open.
  var siblingCount = props.siblingCount;
  var autoOpen = depth < 2 && (siblingCount == null || siblingCount <= AUTO_EXPAND_SIBLING_LIMIT);
  var [open, setOpen] = useState(autoOpen);
  var [setVarOpen, setSetVarOpen] = useState(false);

  var isObj = value !== null && value !== undefined && typeof value === 'object';
  var isArr = Array.isArray(value);
  var keys = isObj ? Object.keys(value) : [];
  var count = keys.length;
  var indent = depth * 14;
  var displayLabel = getJsonNodeLabel(label, depth, isArr);

  // The "set to variable" affordance renders only when the host wired the
  // callback (the Variables panel reuses this tree read-only and passes none).
  var canSetVar = typeof onSetToVariable === 'function';
  var trigger = canSetVar
    ? setVarTriggerButton(setVarOpen, function() { setSetVarOpen(function(v) { return !v; }); })
    : null;
  // The chooser expands in place beneath the row (no popup / overlay).
  var chooser = canSetVar && setVarOpen
    ? h(SetToVariableForm, {
        path: path, value: value, variableNames: variableNames,
        onSetToVariable: onSetToVariable, indent: indent,
        onClose: function() { setSetVarOpen(false); }
      })
    : null;

  if (!isObj) {
    var leafRow = h('div', { className: 'jt-row', style: { paddingLeft: indent + 'px' } },
      h('span', { className: 'jt-spacer' }),
      label != null ? h('span', { className: 'jt-key' }, displayLabel) : null,
      label != null ? h('span', { className: 'jt-separator' }, ':') : null,
      h(JsonLeaf, { value: value }),
      trigger
    );
    // Wrap only when the chooser can appear, so read-only callers are untouched.
    return canSetVar ? h('div', { className: 'jt-leaf' }, leafRow, chooser) : leafRow;
  }

  return h('div', { className: 'jt-group' },
    h('div', {
      className: 'jt-row jt-row-toggle',
      style: { paddingLeft: indent + 'px' }
    },
      h('button', {
        type: 'button',
        className: 'jt-toggle-btn',
        onClick: function(e) {
          e.stopPropagation();
          setOpen(function(v) { return !v; });
        },
        'aria-label': open ? 'Collapse ' + displayLabel : 'Expand ' + displayLabel,
        title: open ? 'Collapse' : 'Expand'
      }, open ? '-' : '+'),
      h('span', { className: 'jt-key' }, displayLabel),
      h('span', { className: 'jt-preview' }, getJsonNodeSummary(isArr, count)),
      trigger
    ),
    chooser,
    open ? keys.map(function(k) {
      var childPath = path ? path + '.' + k : String(k);
      return h(JsonTreeNode, {
        key: k,
        label: k,
        value: value[k],
        depth: depth + 1,
        path: childPath,
        siblingCount: count,
        variableNames: variableNames,
        onSetToVariable: onSetToVariable
      });
    }) : null
  );
}

module.exports = {
  JsonLeaf: JsonLeaf,
  JsonTreeNode: JsonTreeNode,
  getJsonNodeLabel: getJsonNodeLabel,
  getJsonNodeSummary: getJsonNodeSummary,
  getAutoExpandedPickerRoot: getAutoExpandedPickerRoot
};
